import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import axios from 'axios';
import moment from 'moment';
import { Segment, Dimmer, Loader, Message } from 'semantic-ui-react';

import BasicReport from 'dyzio-shared-components/lib/reports/basic-report';
import TopHeader from 'dyzio-shared-components/lib/components/top-header';

import UserDetailsModal from '../../components/user-details-modal';

const REPORT_TIMEOUT = 50000;
const DEFAULT_PER_DAYS = 90;
const PROFILE_KEY = 'DYZIO_PROFILE';

const IS_PRODUCTION = process.env.NODE_ENV === 'production';
const PROFILE_REQUIRED_DOMAINS = [
  'reports.dyzio.co',
  'share.dyzio.co',
];

const reportTypes = {
  gi: 'Campaign',
  basic: 'Campaign',
  campaign: 'Campaign',
  instagram: 'Instagram',
  instagram2: 'Instagram2',
  auth: 'Authorisation Status',
  insights: 'Insights',
  roi: 'ROI',
  paid: 'Paid',
  emv: 'Earned Media Value (EMV)',
  ga: 'Google Analytics',
  shopify: 'Shopify',
};

const loadingSegmentStyle = { height: 175 };

const daysOptions = [
  { label: '30 days', value: 30, id: 30 },
  { label: '90 days', value: 90, id: 90 },
  { label: '120 days', value: 120, id: 120 },
  { label: 'all days', value: -1, id: 'all' },
];

const isBasicReport = type => _.includes(['basic', 'campaign', 'super'], _.toLower(type));
const shouldReloadOnPeriodFilter = arg => !_.isNull(arg.start) && !_.isNull(arg.end);

class Report extends React.Component {
  constructor(props) {
    super(props);
    const { dayFilterValue } = props.match.params;
    const reportPerDays = _.find(daysOptions, ['value', _.toNumber(dayFilterValue || DEFAULT_PER_DAYS)]);
    this.state = {
      type: null,
      id: null,
      error: false,
      profile: null,
      campaign: null,
      isLoadingReport: false,
      isLoadingCampaign: true,
      isLoadingMarketingCampaigns: false,
      isReportLoaded: false,
      loadingMarketingCampaignsFailed: false,
      data: null,
      sort: 'values.totals.followersCount',
      groupBy: 'influencer',
      order: {
        basic: {
          field: null,
          value: null,
        },
      },
      reportPerDays,
      datePeriodFilter: {
        start: null,
        end: null,
      },
      filter: {
        basic: {
          platform: null,
          handle: null,
        },
      },
    };

    // TODO Generate token, refactor this code
    this.client = axios.create({
      baseURL: process.env.REACT_APP_REPORT_API_URL,
      timeout: REPORT_TIMEOUT,
    });
    this.notifyClient = axios.create({
      baseURL: process.env.REACT_APP_NOTIFY_API_URL,
      timeout: 2000,
      headers: {
        Authorization: 'Bearer eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJhcHBfbWV0YWRhdGEiOnsicm9sZXMiOlsidXNlciJdLCJkeXppbyI6eyJ1X2lkIjoiZGVhNDVhMmMtYWQ0MS00OGQ2LWIxYjQtOGFlZjQ1NjZjNGZiIiwibmFtZSI6IkRhbiBDYW50aXIiLCJlbWFpbCI6ImRhbi5jYW50aXJAYXRvbWF0ZS5uZXQiLCJvX2lkIjoiZjE0ZDM5MzEtMGU2YS00MmU4LWFkM2MtYWUyZmYwN2UzZDk2Iiwicm9sZSI6IlRFQU1fTUVNQkVSIiwidmVyc2lvbiI6MX19LCJpc3MiOiJodHRwczovL2R5emlvLmV1LmF1dGgwLmNvbS8iLCJzdWIiOiJhdXRoMHw1OGI1NmMxYmIzM2M1NTQ2Nzg3YWE0NTYiLCJhdWQiOiJNeHVFWmthWHF1Wm5sSmVMOW9FOFFoeVF6aE10amZUcCIsImlhdCI6MTUwOTcwNzU2NCwiZXhwIjoxNTA5NzQzNTY0fQ.sIKlNtois5uMAKWrGD4tsfiNtT7JsqSnamFVRmRFXmg',
      },
    });
  }

  componentDidMount = () => {
    const { id } = this.props.match.params;

    const profile = this.getProfile();
    this.setState({
      profile,
    }, () => {
      this.client.get(`rv/${id}`).then(response => {
        const {
          data: {
            data: {
              name,
              hashtags,
              mentions,
              createdBy,
            },
          },
        } = response.data;
        this.setState({
          isLoadingCampaign: false,
          isLoadingReport: true,
          campaign: {
            id,
            name,
            hashtags,
            mentions,
            createdBy,
          } }, () => {
          this.fetchReport();
        });
      });
    });
  }

  onSortChanged = (event, data) => {
    const { value } = data;
    this.setState({
      sort: value,
    });
  }

  onDateFilterChange = (date, dateType) => {
    const { datePeriodFilter } = this.state;
    datePeriodFilter[dateType] = moment(date);
    this.setState({
      datePeriodFilter,
    }, () => {
      if (shouldReloadOnPeriodFilter(this.state.datePeriodFilter)) {
        this.fetchReport();
      }
    });
  }

  onReportPerDaysChanged = (e, option) => {
    const { id, type } = this.props.match.params;
    this.props.history.push({
      pathname: `/report/${id}/${type}/${option.value}`,
    });
    this.setState({
      reportPerDays: option,
      isLoadingReport: true,
      isReportLoaded: false,
    }, this.fetchReport);
  }

  onOrderChanged = (field, value) => {
    const { order } = this.state;
    order.basic = {
      field,
      value,
    };
    this.setState({
      order,
    });
  }

  onGroupByChanged = (event, data) => {
    const { value } = data;
    this.setState({
      groupBy: value,
    });
  }

  onFilterChange = (event, data) => {
    const handle = _.get(data, 'children[2]');
    const { filter } = this.state;
    const { value } = data;
    const parts = _.split(value, '.');
    const val = parts.pop();
    const key = parts.pop();
    filter.basic[key] = val === 'reset' ? null : val;
    filter.instagramHandle = val === 'reset' ? undefined : handle;
    this.setState({
      filter,
    });
  }

  getProfile = () => {
    // Retrieves the profile data from localStorage
    const profile = localStorage.getItem(PROFILE_KEY);
    return profile ? JSON.parse(profile) : null;
  }

  setProfile = profile => {
    localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
    this.setState({
      profile,
    }, this.notify);
  }


  notify = () => {
    const { profile, campaign } = this.state;
    const { href } = window.location;

    if (profile) {
      const notification = {
        data: {
          profile,
          campaign,
          href,
        },
      };

      if (IS_PRODUCTION) {
        this.notifyClient.post('/', notification)
          .then(() => console.log('notified OK.'))
          .catch(e => console.log(e));
      } else {
        console.log(`no notifications on ${window.location.hostname}`);
      }
    }
  }


  hasMarketingCampaigns = marketingCampaigns => marketingCampaigns && !_.isEmpty(marketingCampaigns)

  fetchReport = forceRefresh => {
    const { id, type } = this.props.match.params;
    const { profile, reportPerDays, datePeriodFilter } = this.state;

    this.setState({
      isLoadingReport: true,
      isReportLoaded: false,
    });

    const daysFilter = reportPerDays ? reportPerDays.value : DEFAULT_PER_DAYS;
    const shouldFilterByDays = false;

    const { start: startDate, end: endDate } = datePeriodFilter;
    const canFilterByDatePeriod = !_.isNull(startDate) && !_.isNull(endDate);

    const params = {
      type,
      id,
      days: shouldFilterByDays ? daysFilter : undefined,
      startDate: canFilterByDatePeriod ? startDate.format('YYYY-MM-DD') : undefined,
      endDate: canFilterByDatePeriod ? endDate.format('YYYY-MM-DD') : undefined,
    };

    if (forceRefresh) {
      params.forceRefresh = true;
    }
    this.client.get(`reportview/${id}`).then(response => {
      const { data } = response;
      if (isBasicReport(type)) {
        const posts = _.filter(data.data.posts, (post => !_.isNull(post) && !_.isUndefined(post.trackedAsset)));
        data.data.posts = posts;
      }
      this.setState({
        isLoadingReport: false,
        isReportLoaded: true,
        data,
        id,
        type,
        profile,
      }, this.notify);
    })
      .catch(error => {
        console.log(error.message);
        this.setState({
          isLoadingReport: false,
          isReportLoaded: true,
          error,
          profile,
          campaign: { name: 'error loading campaign' },
        }, this.notify);
      });
  }

  forceRefresh = () => {
    const forceRefresh = true;
    return this.fetchReport(forceRefresh);
  }

  render() {
    const {
      data,
      error,
      sort,
      filter,
      order,
      profile,
      reportPerDays,
      campaign,
      isLoadingCampaign,
      isLoadingReport,
      isReportLoaded,
      datePeriodFilter: datePeriodFilterState,
      ads,
      isLoadingMarketingCampaigns,
      loadingMarketingCampaignsFailed,
    } = this.state;

    const type = 'campaign';

    const isACR = campaign && campaign.campaignType === 'ACR';

    const bottomSpace = { height: 100, width: '100%' };
    const isProfileSet = Boolean(profile);
    const isProfileRequired = _.includes(PROFILE_REQUIRED_DOMAINS, window.location.hostname);

    const hasMarketingCampaign = this.hasMarketingCampaigns(campaign ? campaign.marketingCampaigns : null);


    const topHeaderProps = {
      name: _.get(campaign, 'name', ''),
      createdBy: _.get(campaign, 'createdBy', ''),
      headerInfo: {
        hasLogo: false,
        campaignName: _.get(campaign, 'name', ''),
        reportCountry: _.get(campaign, 'country'),
        reportType: reportTypes[type] || 'Campaign',
        hashtags: _.get(campaign, 'hashtags', ''),
      },
    };

    const shouldUseCustomMessage = isACR && _.includes([30, 90], _.toNumber(reportPerDays.value));
    const emptyCampaignText = shouldUseCustomMessage
      ? `No posts published in the last ${reportPerDays.value} days. Try increasing the days filter above to fetch earlier posts`
      : 'No posts included in this campaign';

    const datePeriodFilter = {
      start: datePeriodFilterState.start && datePeriodFilterState.start.format('DD/MM/YYYY'),
      end: datePeriodFilterState.end && datePeriodFilterState.end.format('DD/MM/YYYY'),
    };

    return (
      <div>

        <TopHeader
          isLoading={isLoadingCampaign}
          {...topHeaderProps}
          onForceRefresh={this.forceRefresh}
        />

        {
          isBasicReport(type) && isReportLoaded &&
          <div>
            <BasicReport
              data={data}
              sort={sort}
              order={order.basic}
              filter={filter.basic}
              instagramHandleFilter={_.get(this.state, 'filter.instagramHandle')}
              emptyCampaignText={emptyCampaignText}
              emptyCampaignMessageType={shouldUseCustomMessage ? 'warning' : 'info'}
              perDaysOption={reportPerDays}
              withDaysFilter={isACR}
              onPerDaysChanged={this.onReportPerDaysChanged}
              hasRightFilterOptions={false}
              onFilterChange={this.onFilterChange}
              daysOptions={daysOptions}
              onOrderChanged={this.onOrderChanged}
              withDatePeriodFilter={!isACR}
              datePeriodFilterValues={datePeriodFilter}
              onDateFilterChange={this.onDateFilterChange}
              onForceRefresh={this.forceRefresh}
              hasMarketingCampaigns={hasMarketingCampaign}
              isLoadingMarketingCampaigns={isLoadingMarketingCampaigns}
              loadingMarketingCampaignsFailed={loadingMarketingCampaignsFailed}
              ads={ads}
              shouldRenderShareableLinks={false}
            />
          </div>
        }

        {
          isReportLoaded && error && <Message
            error
            icon="warning circle"
            header="Something went wrong"
            content="Could not load report. Please try again later"
          />}

        {
          isProfileRequired &&
          <UserDetailsModal
            open={!isProfileSet}
            successHandler={this.setProfile}
          />
        }

        {
          isLoadingReport &&
          <Segment style={loadingSegmentStyle}>
            <Dimmer active inverted>
              <Loader size="big">Loading report</Loader>
            </Dimmer>
          </Segment>
        }

        <div style={bottomSpace} />
      </div>
    );
  }
}

Report.propTypes = {
  match: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};

Report.contextTypes = {
  router: PropTypes.object,
};

export default Report;



// WEBPACK FOOTER //
// ./src/containers/report-view/index.js